import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { useQuery } from "@tanstack/react-query";

import { searchClientifyContacts } from "../../../services/clientifyService";

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    minHeight: 40,
    fontSize: "0.875rem",
    py: "2px !important",
  },
};

const contactLabel = (contact) => {
  if (!contact) return "";
  const name =
    contact.full_name ||
    [contact.first_name, contact.last_name].filter(Boolean).join(" ") ||
    contact.name ||
    `Contacto #${contact.id}`;
  const email = contact.email || contact.emails?.[0]?.email;
  return email ? `${name} <${email}>` : name;
};

/** Contact picker searching Clientify as the user types. */
export const ContactAutocomplete = ({ value, onChange, ownerId, disabled }) => {
  const [input, setInput] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setSearch(input.trim()), 300);
    return () => clearTimeout(timer);
  }, [input]);

  const { data: results = [], isFetching } = useQuery({
    queryKey: ["clientify-contact-search", search, ownerId],
    queryFn: () => searchClientifyContacts(search, ownerId),
    enabled: !disabled && search.length >= 2,
    staleTime: 60 * 1000,
  });

  return (
    <Autocomplete
      size="small"
      value={value}
      options={results}
      disabled={disabled}
      filterOptions={(options) => options}
      getOptionLabel={contactLabel}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      onChange={(_, next) => onChange(next)}
      inputValue={input}
      onInputChange={(_, next) => setInput(next)}
      loading={isFetching}
      noOptionsText={search.length < 2 ? "Escribe al menos 2 letras" : "Sin resultados"}
      loadingText="Buscando..."
      slotProps={{ popper: { sx: { zIndex: 1400 } } }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Buscar contacto"
          sx={inputSx}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isFetching ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

ContactAutocomplete.propTypes = {
  value: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  ownerId: PropTypes.number,
  disabled: PropTypes.bool,
};

/** Free-text tags, one chip per entry. */
export const TagsInput = ({ value, onChange, disabled }) => (
  <Autocomplete
    multiple
    freeSolo
    size="small"
    options={[]}
    value={value}
    disabled={disabled}
    onChange={(_, next) => onChange(next.map((tag) => String(tag).trim()).filter(Boolean))}
    slotProps={{ popper: { sx: { zIndex: 1400 } } }}
    renderInput={(params) => (
      <TextField {...params} placeholder="Escribe y pulsa Enter" sx={inputSx} />
    )}
  />
);

TagsInput.propTypes = {
  value: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};
